import { updateDoc, doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import { cleanForFirestore } from '../utils/firestoreClean';
import { normalizeAnswers, normalizeAwarded, safeSetAssignee } from '../utils/firestoreWrite';
import type { Assignee } from './assignmentsService';
import { normalizeQuestionType } from '../utils/questionTypeNormalization';

// Types for auto-grading
interface AutoGradingResult {
  score: number;
  totalPossible: number;
  percentage: number;
  gradedQuestions: GradedQuestion[];
  needsManualReview: GradedQuestion[];
  confidence: 'high' | 'medium' | 'low';
  gradingTimestamp: Date;
}

interface GradedQuestion {
  questionId: string;
  type: string;
  studentAnswer: any;
  correctAnswer: any;
  pointsAwarded: number;
  totalPoints: number;
  isCorrect: boolean;
  confidence: 'high' | 'medium' | 'low';
  feedback?: string;
  needsReview: boolean;
}

interface QuestionData {
  id: string;
  type: 'mcq' | 'true_false' | 'short' | 'multipart' | 'matching' | 'multiple_select';
  text: string;
  choices?: Array<{
    id: string;
    text: string;
    isCorrect: boolean;
  }>;
  correctAnswer?: string | boolean;
  correctAnswers?: string[];
  pairs?: Array<{
    left: string;
    right: string;
  }>;
  points: number;
  parts?: Array<{
    key: string;
    marks: number;
    type: 'text' | 'number' | 'mcq';
    answer?: string;
    numeric?: { value: number; tolerance?: number; sigFigs?: number };
  }>;
}

interface StudentSubmission {
  assignmentId: string;
  studentId: string;
  answers: Record<string, any>;
  submittedAt: Date;
  questions: QuestionData[];
}

/**
 * Auto-grade multiple choice questions
 */
function gradeMultipleChoice(question: QuestionData, studentAnswer: string): GradedQuestion {
  const correctChoice = question.choices?.find(choice => choice.isCorrect);
  const isCorrect = studentAnswer === correctChoice?.id;

  return {
    questionId: question.id,
    type: 'mcq',
    studentAnswer,
    correctAnswer: correctChoice?.id,
    pointsAwarded: isCorrect ? question.points : 0,
    totalPoints: question.points,
    isCorrect,
    confidence: 'high', // MC questions are 100% confident
    needsReview: false,
    feedback: isCorrect
      ? 'Correct!'
      : `Incorrect. The correct answer is ${correctChoice?.text}`
  };
}

/**
 * Auto-grade true/false questions
 */
function gradeTrueFalse(question: QuestionData, studentAnswer: boolean): GradedQuestion {
  const isCorrect = studentAnswer === question.correctAnswer;

  return {
    questionId: question.id,
    type: 'true_false',
    studentAnswer,
    correctAnswer: question.correctAnswer,
    pointsAwarded: isCorrect ? question.points : 0,
    totalPoints: question.points,
    isCorrect,
    confidence: 'high', // T/F questions are 100% confident
    needsReview: false,
    feedback: isCorrect
      ? 'Correct!'
      : `Incorrect. The correct answer is ${question.correctAnswer ? 'True' : 'False'}`
  };
}

/**
 * Auto-grade multiple select questions
 */
function gradeMultipleSelect(question: QuestionData, studentAnswers: string[]): GradedQuestion {
  const correctAnswers = question.correctAnswers || [];
  const studentSet = new Set(studentAnswers);
  const correctSet = new Set(correctAnswers);

  // Check if sets are identical
  const isFullyCorrect = studentSet.size === correctSet.size &&
    [...studentSet].every(answer => correctSet.has(answer));

  // Partial credit calculation
  const correctSelections = studentAnswers.filter(answer => correctSet.has(answer)).length;
  const incorrectSelections = studentAnswers.filter(answer => !correctSet.has(answer)).length;
  const missedCorrect = correctAnswers.filter(answer => !studentSet.has(answer)).length;

  // Award partial credit: correct selections minus incorrect selections
  const partialScore = Math.max(0, correctSelections - incorrectSelections) / correctAnswers.length;
  const pointsAwarded = Math.round(partialScore * question.points);

  return {
    questionId: question.id,
    type: 'multiple_select',
    studentAnswer: studentAnswers,
    correctAnswer: correctAnswers,
    pointsAwarded,
    totalPoints: question.points,
    isCorrect: isFullyCorrect,
    confidence: 'high',
    needsReview: false,
    feedback: isFullyCorrect
      ? 'Correct!'
      : `Partial credit: ${correctSelections} correct, ${incorrectSelections} incorrect, ${missedCorrect} missed`
  };
}

/**
 * Auto-grade short answer questions - ALWAYS require manual review (Stage 1)
 */
function gradeShortAnswer(question: QuestionData, studentAnswer: string): GradedQuestion {
  return {
    questionId: question.id,
    type: 'short',
    studentAnswer,
    correctAnswer: question.correctAnswer || null,
    pointsAwarded: 0,
    totalPoints: question.points,
    isCorrect: false,
    confidence: 'low',
    needsReview: true,
    feedback: 'Awaiting teacher review'
  };
}

/**
 * Auto-grade fill in the blank questions - exact match
 */
function gradeFillBlank(question: QuestionData, studentAnswer: string): GradedQuestion {
  const correctAnswer = question.correctAnswer as string;

  if (!correctAnswer || !studentAnswer) {
    return {
      questionId: question.id,
      type: 'fill_blank',
      studentAnswer,
      correctAnswer,
      pointsAwarded: 0,
      totalPoints: question.points,
      isCorrect: false,
      confidence: 'high',
      needsReview: false,
      feedback: 'No answer provided'
    };
  }

  // Normalize for comparison (case-insensitive, trim whitespace)
  const normalize = (text: string) => text.toLowerCase().trim();
  const normalizedStudent = normalize(studentAnswer);
  const normalizedCorrect = normalize(correctAnswer);

  const isCorrect = normalizedStudent === normalizedCorrect;

  return {
    questionId: question.id,
    type: 'fill_blank',
    studentAnswer,
    correctAnswer,
    pointsAwarded: isCorrect ? question.points : 0,
    totalPoints: question.points,
    isCorrect,
    confidence: 'high',
    needsReview: false,
    feedback: isCorrect
      ? 'Correct!'
      : `Incorrect. Expected: ${correctAnswer}`
  };
}

/**
 * Mark essay questions for manual review - no auto-grading
 */
function gradeEssay(question: QuestionData, studentAnswer: string): GradedQuestion {
  return {
    questionId: question.id,
    type: 'essay',
    studentAnswer,
    correctAnswer: null,
    pointsAwarded: 0,
    totalPoints: question.points,
    isCorrect: false,
    confidence: 'low',
    needsReview: true,
    feedback: 'Awaiting teacher review'
  };
}

/**
 * Auto-grade matching questions
 * studentAnswer format: { [leftTerm]: rightDefinition }
 */
function gradeMatching(question: QuestionData, studentAnswer: Record<string, string>): GradedQuestion {
  const pairs = question.pairs || [];

  if (pairs.length === 0) {
    return {
      questionId: question.id,
      type: 'matching',
      studentAnswer,
      correctAnswer: null,
      pointsAwarded: 0,
      totalPoints: question.points,
      isCorrect: false,
      confidence: 'high',
      needsReview: false,
      feedback: 'No pairs defined for this matching question'
    };
  }

  // Count correct matches
  let correctMatches = 0;
  const totalPairs = pairs.length;

  for (const pair of pairs) {
    // Check if student matched this left term to the correct right definition
    if (studentAnswer[pair.left] === pair.right) {
      correctMatches++;
    }
  }

  const isFullyCorrect = correctMatches === totalPairs;
  const partialScore = correctMatches / totalPairs;
  const pointsAwarded = Math.round(partialScore * question.points);

  return {
    questionId: question.id,
    type: 'matching',
    studentAnswer,
    correctAnswer: pairs, // Return pairs as correct answer reference
    pointsAwarded,
    totalPoints: question.points,
    isCorrect: isFullyCorrect,
    confidence: 'high',
    needsReview: false,
    feedback: isFullyCorrect
      ? 'All matches correct!'
      : `${correctMatches}/${totalPairs} matches correct`
  };
}

/**
 * Main auto-grading function
 */
export async function autoGradeSubmission(submission: StudentSubmission): Promise<AutoGradingResult> {
  const gradedQuestions: GradedQuestion[] = [];
  const needsManualReview: GradedQuestion[] = [];
  let totalScore = 0;
  let totalPossible = 0;

  // Process each question
  for (const question of submission.questions) {
    const studentAnswer = submission.answers[question.id];
    let gradedQuestion: GradedQuestion;

    totalPossible += question.points;

    // Normalize the type for consistent comparison
    const normalizedType = normalizeQuestionType(question.type);

    switch (normalizedType) {
      case 'multiple-choice':
        gradedQuestion = gradeMultipleChoice(question, studentAnswer);
        break;

      case 'true-false':
        gradedQuestion = gradeTrueFalse(question, studentAnswer);
        break;

      case 'multiple-select':
        gradedQuestion = gradeMultipleSelect(question, studentAnswer || []);
        break;

      case 'short-answer':
        gradedQuestion = gradeShortAnswer(question, studentAnswer);
        break;

      case 'fill-blank':
        gradedQuestion = gradeFillBlank(question, studentAnswer);
        break;

      case 'essay':
        gradedQuestion = gradeEssay(question, studentAnswer);
        break;

      case 'matching':
        gradedQuestion = gradeMatching(question, studentAnswer || {});
        break;

      default:
        // Unknown question type - needs manual review
        gradedQuestion = {
          questionId: question.id,
          type: question.type,
          studentAnswer,
          correctAnswer: null,
          pointsAwarded: 0,
          totalPoints: question.points,
          isCorrect: false,
          confidence: 'low',
          needsReview: true,
          feedback: `Unknown question type: ${question.type}`
        };
    }

    gradedQuestions.push(gradedQuestion);
    totalScore += gradedQuestion.pointsAwarded;

    if (gradedQuestion.needsReview) {
      needsManualReview.push(gradedQuestion);
    }
  }

  // Calculate overall confidence
  const highConfidenceCount = gradedQuestions.filter(q => q.confidence === 'high').length;
  const totalQuestions = gradedQuestions.length;
  let overallConfidence: 'high' | 'medium' | 'low';

  if (highConfidenceCount / totalQuestions > 0.8) {
    overallConfidence = 'high';
  } else if (highConfidenceCount / totalQuestions > 0.5) {
    overallConfidence = 'medium';
  } else {
    overallConfidence = 'low';
  }

  return {
    score: totalScore,
    totalPossible,
    percentage: Math.round((totalScore / totalPossible) * 100),
    gradedQuestions,
    needsManualReview,
    confidence: overallConfidence,
    gradingTimestamp: new Date()
  };
}

/**
 * Update assignment with auto-grading results
 */
export async function updateAssignmentWithGrading(
  assignmentId: string,
  studentId: string,
  gradingResult: AutoGradingResult
): Promise<void> {
  try {
    const ref = doc(db, 'assignments', assignmentId, 'assignees', studentId);

    const payload: Partial<Assignee> = {
      status: 'submitted',
      autoGraded: true,
      gradingResult: {
        score: Number.isFinite(gradingResult.score) ? gradingResult.score : 0,
        totalPossible: Number.isFinite(gradingResult.totalPossible) ? gradingResult.totalPossible : 0,
        percentage: Number.isFinite(gradingResult.percentage) ? gradingResult.percentage : 0,
        confidence: gradingResult.confidence || 'low',
        needsManualReview: gradingResult.needsManualReview?.length > 0 || false,
        reviewQuestions: gradingResult.needsManualReview?.map(q => q.questionId) || [],
        gradedQuestions: gradingResult.gradedQuestions || [],
      },
      progress: {
        autoGraded: true,
        finalScore: Number.isFinite(gradingResult.score) ? gradingResult.score : 0,
        totalPossible: Number.isFinite(gradingResult.totalPossible) ? gradingResult.totalPossible : 0,
        percentage: Number.isFinite(gradingResult.percentage) ? gradingResult.percentage : 0,
      }
    };

    // Use safe wrapper that normalizes and cleans everything
    await safeSetAssignee(ref, payload);

    console.log(`✅ Auto-graded assignment ${assignmentId} for student ${studentId}: ${gradingResult.score}/${gradingResult.totalPossible}`);

    // TODO: Send notification to teacher
    await notifyTeacherOfGrading(assignmentId, studentId, gradingResult);

  } catch (error) {
    console.error('Error updating assignment with grading:', error);
    throw error;
  }
}

/**
 * Send notification to teacher about auto-grading completion
 */
async function notifyTeacherOfGrading(
  assignmentId: string,
  studentId: string,
  gradingResult: AutoGradingResult
): Promise<void> {
  try {
    // Get student and assignment info for notification
    const assigneeRef = doc(db, 'assignees', `${assignmentId}_${studentId}`);
    const assigneeDoc = await getDoc(assigneeRef);

    if (!assigneeDoc.exists()) {
      console.error('Assignee document not found for notification');
      return;
    }

    const assignmentRef = doc(db, 'assignments', assignmentId);
    const assignmentDoc = await getDoc(assignmentRef);

    if (!assignmentDoc.exists()) {
      console.error('Assignment document not found for notification');
      return;
    }

    const assignmentData = assignmentDoc.data();
    const teacherId = assignmentData.teacherId;

    // Get student info
    const studentRef = doc(db, 'users', studentId);
    const studentDoc = await getDoc(studentRef);
    const studentName = studentDoc.exists()
      ? `${studentDoc.data().firstName} ${studentDoc.data().lastName}`
      : 'Student';

    // Create notification (you'll need to implement your notification system)
    const notification = {
      teacherId,
      type: 'auto_grading_complete',
      title: '🤖 Auto-Grading Complete',
      message: `${studentName} completed "${assignmentData.title}" - Score: ${gradingResult.score}/${gradingResult.totalPossible} (${gradingResult.percentage}%)`,
      needsReview: gradingResult.needsManualReview.length > 0,
      reviewCount: gradingResult.needsManualReview.length,
      assignmentId,
      studentId,
      createdAt: serverTimestamp(),
      read: false
    };

    // TODO: Add to your notifications collection
    console.log('📧 Teacher notification:', notification);

  } catch (error) {
    console.error('Error sending teacher notification:', error);
  }
}

/**
 * Process submission with auto-grading (main entry point)
 */
export async function processSubmissionWithAutoGrading(
  assignmentId: string,
  studentId: string,
  answers: Record<string, any>,
  questions: QuestionData[]
): Promise<AutoGradingResult> {

  const submission: StudentSubmission = {
    assignmentId,
    studentId,
    answers,
    submittedAt: new Date(),
    questions
  };

  // Perform auto-grading
  const gradingResult = await autoGradeSubmission(submission);

  // Update assignment with results
  await updateAssignmentWithGrading(assignmentId, studentId, gradingResult);

  return gradingResult;
}